import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Grid,
  Button, Box, Stack, Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CakeIcon from '@mui/icons-material/Cake';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StraightenIcon from '@mui/icons-material/Straighten';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import candidates from '../data/candidates.json';
import candidateImages from '../assets/candidateImages';
import { useTranslation } from 'react-i18next';

const getProfesiColor = (profesi = '') => {
  const p = profesi.toLowerCase();
  if (p.includes('art') || p.includes('asisten') || p.includes('household')) return '#2563eb';
  if (p.includes('baby') || p.includes('nanny') || p.includes('sitter'))      return '#f97316';
  return '#059669';
};

const PROFESI_TKEY = {
  'Asisten Rumah Tangga': 'kandidatBanner.profesi.art',
  'Babysitter': 'kandidatBanner.profesi.babysitter',
  'Perawat Lansia': 'kandidatBanner.profesi.elderCare',
};

const getProfesiLabel = (profesi, t) => {
  const tKey = PROFESI_TKEY[profesi];
  return tKey ? t(tKey) : profesi;
};

const getLightColor = (color) => {
  if (color === '#2563eb') return '#93c5fd';
  if (color === '#f97316') return '#fdba74';
  return '#6ee7b7';
};

function SectionCard({ children, sx = {} }) {
  return (
    <Box sx={{
      bgcolor: '#fff',
      borderRadius: '20px',
      p: { xs: 3, md: 3.5 },
      boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
      border: '1px solid #f1f5f9',
      ...sx,
    }}>
      {children}
    </Box>
  );
}

function SectionLabel({ children, accent }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontWeight: 900, fontSize: '1rem', color: '#0f172a', letterSpacing: '-0.02em', mb: 0.8 }}>
        {children}
      </Typography>
      <Box sx={{ width: 36, height: 3, bgcolor: accent || '#f97316', borderRadius: 1 }} />
    </Box>
  );
}

export default function KandidatDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const { t } = useTranslation();
  const currentIndex = candidates.findIndex((c) => c.slug === slug);
  const k = candidates[currentIndex];
  const prev = currentIndex > 0 ? candidates[currentIndex - 1] : null;
  const next = currentIndex < candidates.length - 1 ? candidates[currentIndex + 1] : null;

  if (!k) {
    return (
      <Box sx={{ py: 20, textAlign: 'center', bgcolor: '#f8fafc', minHeight: '100vh' }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>{t('kandidat.detail.notFound')}</Typography>
        <Button component={Link} to="/kandidat" sx={{ mt: 2, color: '#2563eb' }}>{t('kandidat.detail.backButton')}</Button>
      </Box>
    );
  }

  const color = getProfesiColor(k.profesi);
  const lightColor = getLightColor(color);

  const waHref = `https://wa.me/${t('footer.number')}?text=${encodeURIComponent(
    t('kandidat.detail.ctaMessage', { name: k.namaLengkap, profesi: k.profesi })
  )}`;

  const personalInfo = [
    { label: t('kandidat.detail.labelPendidikan'), value: k.pendidikanTerakhir },
    { label: t('kandidat.detail.labelAsalKota'),   value: k.asal },
    { label: t('kandidat.detail.labelDomisili'),   value: k.domisili },
    { label: t('kandidat.detail.labelAgama'),      value: k.agama },
    { label: t('kandidat.detail.labelStatus'),     value: k.statusPernikahan },
    { label: t('kandidat.detail.labelAnak'),       value: k.punyaAnak },
  ];

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>

      {/* ── HERO ── */}
      <Box sx={{
        position: 'relative',
        background: [
          `radial-gradient(ellipse 60% 80% at 8% 60%, ${color}26 0%, transparent 65%)`,
          'radial-gradient(ellipse 50% 60% at 90% 20%, rgba(249,115,22,0.10) 0%, transparent 55%)',
          '#060d1f',
        ].join(', '),
        overflow: 'hidden',
        pt: { xs: 14, md: 10 },
        pb: { xs: 5, md: 7 },
      }}>
        {/* Dot grid */}
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1.5px, transparent 1.5px)',
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(ellipse 90% 90% at 50% 50%, black 20%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 90% at 50% 50%, black 20%, transparent 100%)',
        }} />

        {/* Top accent bar */}
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 4,
          background: 'linear-gradient(90deg, #2563eb, #f97316)',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Navigation row */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Button
              component={Link}
              to="/kandidat"
              startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
              sx={{
                color: 'rgba(255,255,255,0.6)',
                textTransform: 'none',
                fontSize: '0.82rem',
                fontWeight: 600,
                px: 0,
                '&:hover': { color: '#fff', bgcolor: 'transparent' },
              }}
            >
              {t('kandidat.detail.backToList')}
            </Button>

            <Stack direction="row" spacing={1}>
              <Button
                disabled={!prev}
                onClick={() => prev && navigate(`/kandidat/${prev.slug}`)}
                startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 12 }} />}
                sx={{
                  color: prev ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.2)',
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  px: 1.5, py: 0.75,
                  borderRadius: '10px',
                  bgcolor: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.13)', color: '#fff' },
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {prev ? prev.namaPanggilan : t('kandidat.detail.backToList')}
                </Box>
              </Button>

              <Button
                disabled={!next}
                onClick={() => next && navigate(`/kandidat/${next.slug}`)}
                endIcon={<ArrowForwardIosIcon sx={{ fontSize: 12 }} />}
                sx={{
                  color: next ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.2)',
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  px: 1.5, py: 0.75,
                  borderRadius: '10px',
                  bgcolor: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.13)', color: '#fff' },
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {next ? next.namaPanggilan : ''}
                </Box>
              </Button>
            </Stack>
          </Stack>

          {/* Badges row */}
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 1.8 }}>
            {/* Profesi badge */}
            <Box sx={{
              px: 1.5, py: 0.45,
              bgcolor: `${color}22`,
              border: `1px solid ${color}55`,
              borderRadius: '20px',
              color: lightColor,
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
            }}>
              {getProfesiLabel(k.profesi, t)}
            </Box>

            {/* Status badge */}
            <Box sx={{
              px: 1.4, py: 0.45,
              bgcolor: 'rgba(16,185,129,0.15)',
              border: '1px solid rgba(16,185,129,0.28)',
              borderRadius: '20px',
              display: 'flex', alignItems: 'center', gap: 0.6,
            }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981' }} />
              <Typography sx={{ color: '#6ee7b7', fontSize: '0.7rem', fontWeight: 700 }}>
                {k.statusSDM}
              </Typography>
            </Box>

            {/* ID tag */}
            <Box sx={{
              px: 1.4, py: 0.45,
              bgcolor: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '20px',
              color: 'rgba(255,255,255,0.4)',
              fontSize: '0.68rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              display: { xs: 'none', sm: 'block' },
            }}>
              #SDM-{k.slug.toUpperCase().slice(0, 5)}
            </Box>
          </Stack>

          {/* Name */}
          <Typography variant="h2" sx={{
            color: '#fff',
            fontWeight: 900,
            fontSize: { xs: '2.1rem', md: '3.2rem' },
            lineHeight: 1.05,
            letterSpacing: '-0.03em',
            mb: 0.6,
          }}>
            {k.namaPanggilan}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.88rem', letterSpacing: '0.01em' }}>
            {k.namaLengkap}
          </Typography>
        </Container>
      </Box>

      {/* ── MAIN CONTENT ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 }, pb: { xs: 3, md: 4 } }}>
        <Grid container spacing={4} alignItems="flex-start">

          {/* ── LEFT: photo + CTA ── */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 96 } }}>
              <Box sx={{
                borderRadius: '24px',
                overflow: 'hidden',
                boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
                border: '1px solid #e2e8f0',
              }}>

                {/* Photo */}
                <Box sx={{ position: 'relative', height: { xs: 300, md: 380 } }}>
                  <Box
                    component="img"
                    src={candidateImages[k.slug] || k.foto}
                    alt={k.namaLengkap}
                    sx={{
                      width: '100%', height: '100%',
                      objectFit: 'cover', objectPosition: 'center top',
                      display: 'block',
                    }}
                  />
                  <Box sx={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(to top, rgba(15,23,42,0.80) 0%, transparent 55%)',
                  }} />

                  {/* Profesi pill on photo */}
                  <Box sx={{
                    position: 'absolute', top: 14, left: 14,
                    px: 1.4, py: 0.5,
                    bgcolor: 'rgba(10,15,30,0.72)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255,255,255,0.14)',
                    borderRadius: '20px',
                    display: 'flex', alignItems: 'center', gap: 0.7,
                  }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} />
                    <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 700 }}>
                      {getProfesiLabel(k.profesi, t)}
                    </Typography>
                  </Box>

                  {/* Name overlay */}
                  <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 2.5 }}>
                    <Typography sx={{
                      color: '#fff', fontWeight: 900,
                      fontSize: '1.4rem', lineHeight: 1.1,
                      letterSpacing: '-0.02em',
                    }}>
                      {k.namaPanggilan}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.76rem', mt: 0.3 }}>
                      {k.namaLengkap}
                    </Typography>
                  </Box>
                </Box>

                {/* Card body */}
                <Box sx={{ bgcolor: '#fff', p: 3 }}>
                  {/* 2×2 stat grid */}
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1.2,
                    mb: 3,
                  }}>
                    {[
                      { icon: <CakeIcon sx={{ fontSize: 14, color: '#94a3b8' }} />, label: 'Usia',  val: `${k.umur} tahun` },
                      { icon: <StraightenIcon sx={{ fontSize: 14, color: '#94a3b8' }} />, label: 'Tinggi', val: k?.tinggiBadan ? `${k.tinggiBadan} cm` : '–' },
                      { icon: <MonitorWeightIcon sx={{ fontSize: 14, color: '#94a3b8' }} />, label: 'Berat',  val: k?.beratBadan ? `${k.beratBadan} kg` : '–' },
                      { icon: <GroupsIcon sx={{ fontSize: 14, color: '#94a3b8' }} />, label: 'Suku',  val: k.suku },
                    ].map((s, i) => (
                      <Box key={i} sx={{
                        p: 1.4,
                        bgcolor: '#f8fafc',
                        borderRadius: '12px',
                        border: '1px solid #f1f5f9',
                      }}>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.4 }}>
                          {s.icon}
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {s.label}
                          </Typography>
                        </Stack>
                        <Typography sx={{ fontSize: '0.83rem', fontWeight: 800, color: '#0f172a' }}>
                          {s.val}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* WA button */}
                  <Button
                    fullWidth
                    component="a"
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<WhatsAppIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      py: 1.5,
                      borderRadius: '12px',
                      bgcolor: '#0f172a',
                      color: '#fff',
                      fontSize: '0.88rem',
                      fontWeight: 800,
                      textTransform: 'none',
                      boxShadow: '0 6px 20px rgba(15,23,42,0.22)',
                      '&:hover': { bgcolor: color, boxShadow: `0 6px 20px ${color}44` },
                      transition: 'all 0.2s',
                    }}
                  >
                    {t('kandidat.detail.ctaButton')}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* ── RIGHT: detail cards ── */}
          <Grid item xs={12} md={8}>
            <Stack spacing={3}>

              {/* Personal info */}
              <SectionCard>
                <SectionLabel accent={color}>{t('kandidat.detail.sectionPersonal')}</SectionLabel>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
                  gap: 1.2,
                }}>
                  {personalInfo.map((item, idx) => (
                    <Box key={idx} sx={{ p: 1.5, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #f1f5f9' }}>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.4 }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f172a' }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </SectionCard>

              {/* Skills */}
              <SectionCard>
                <SectionLabel accent={color}>{t('kandidat.detail.sectionSkills')}</SectionLabel>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {k.keahlian.map((skill, i) => (
                    <Box key={i} sx={{
                      px: 1.5, py: 0.65,
                      bgcolor: '#f1f5f9',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      color: '#334155',
                    }}>
                      {skill}
                    </Box>
                  ))}
                </Box>
              </SectionCard>

              {/* Experience */}
              <SectionCard>
                <SectionLabel accent={color}>{t('kandidat.detail.sectionExperience')}</SectionLabel>
                <Stack spacing={0} divider={<Divider sx={{ borderColor: '#f1f5f9' }} />}>
                  {k.pengalaman.map((p, i) => (
                    <Box key={i} sx={{ py: 2.5, display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                      {/* Numbered circle */}
                      <Box sx={{
                        width: 34, height: 34, borderRadius: '50%',
                        bgcolor: `${color}14`,
                        border: `2px solid ${color}38`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, mt: 0.3,
                      }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, color }}>
                          {String(i + 1).padStart(2, '0')}
                        </Typography>
                      </Box>

                      {/* Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={0.8} sx={{ mb: 0.5 }}>
                          <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.95rem', lineHeight: 1.3 }}>
                            {p.posisi}
                          </Typography>
                          <Box sx={{
                            px: 1.2, py: 0.3,
                            bgcolor: '#fff7ed',
                            border: '1px solid #fed7aa',
                            borderRadius: '8px',
                            fontSize: '0.7rem', fontWeight: 700, color: '#f97316',
                            whiteSpace: 'nowrap', flexShrink: 0,
                          }}>
                            {p.durasi}
                          </Box>
                        </Stack>
                        <Typography sx={{ color: '#64748b', fontSize: '0.87rem', lineHeight: 1.7 }}>
                          {p.deskripsi}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </SectionCard>

              {/* Notes */}
              {k.catatan && k.catatan.length > 0 && (
                <Box sx={{ p: 3, borderRadius: '20px', bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: '50%',
                      bgcolor: '#fef3c7',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <AssignmentIcon sx={{ color: '#f97316', fontSize: 16 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 800, color: '#92400e', fontSize: '0.9rem' }}>
                      {t('kandidat.detail.sectionNotes')}
                    </Typography>
                  </Stack>
                  <Stack spacing={1.2}>
                    {k.catatan.map((note, i) => (
                      <Stack key={i} direction="row" alignItems="flex-start" spacing={1.2}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#f97316', mt: 0.2, flexShrink: 0 }} />
                        <Typography sx={{ fontWeight: 600, color: '#78350f', fontSize: '0.85rem', lineHeight: 1.65 }}>
                          {note}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* ── BOTTOM NAV ── */}
      <Box sx={{ bgcolor: '#fff', borderTop: '1px solid #f1f5f9', py: 2.5 }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">

            {/* Prev */}
            <Box
              onClick={() => prev && navigate(`/kandidat/${prev.slug}`)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                cursor: prev ? 'pointer' : 'default',
                opacity: prev ? 1 : 0.3,
                transition: 'opacity 0.2s',
                '&:hover': { opacity: prev ? 0.75 : 0.3 },
              }}
            >
              <Box sx={{
                width: 36, height: 36, borderRadius: '50%',
                bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <ArrowBackIosNewIcon sx={{ fontSize: 13, color: '#64748b' }} />
              </Box>
              {prev && (
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>{t('kandidat.detail.prevCandidate')}</Typography>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: '#0f172a' }}>{prev.namaPanggilan}</Typography>
                </Box>
              )}
            </Box>

            {/* Candidate counter */}
            <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 600 }}>
              {currentIndex + 1} / {candidates.length}
            </Typography>

            {/* Next */}
            <Box
              onClick={() => next && navigate(`/kandidat/${next.slug}`)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                cursor: next ? 'pointer' : 'default',
                opacity: next ? 1 : 0.3,
                transition: 'opacity 0.2s',
                '&:hover': { opacity: next ? 0.75 : 0.3 },
              }}
            >
              {next && (
                <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>{t('kandidat.detail.nextCandidate')}</Typography>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: '#0f172a' }}>{next.namaPanggilan}</Typography>
                </Box>
              )}
              <Box sx={{
                width: 36, height: 36, borderRadius: '50%',
                bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <ArrowForwardIosIcon sx={{ fontSize: 13, color: '#64748b' }} />
              </Box>
            </Box>

          </Stack>
        </Container>
      </Box>
    </Box>
  );
}
